import type { DocumentoCargaCadastro } from "@atlas/sdk";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { atlas } from "../lib/sdk";

// Bloco "Documentos de carga" do modal de prefeitura — os anexos do Passo 6 do
// cadastro do site institucional (arquivos do convenio).
//
// Separado de `DocumentosCadastro` de proposito, e nao um quarto item na lista
// dele: os tres do Passo 5 sao um conjunto FIXO cuja ausencia e' um defeito do
// envio ("falta a selfie"), com miniatura pra conferir identidade. Aqui a
// quantidade e' variavel, a etapa e' OPCIONAL — lista vazia nao e' problema
// nenhum — e o conteudo (planilha, ZIP, PDF operacional) nao tem miniatura util:
// o operador quer o arquivo no disco. Misturar os dois faria o bloco mentir
// sobre o que conta como "envio completo".
//
// So existe na prefeitura: `documentosCarga` e' recusado no cadastro de banco
// pela propria API.

/** Formata o tamanho gravado no cadastro. Sobe pra MB acima de 1024 KB porque o
 *  teto do Passo 6 e' 5 MB por arquivo e "4823 KB" nao se le. */
function tamanhoLegivel(bytes?: number): string | null {
  if (bytes == null) return null;
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(0)} KB`;
}

/** Icone por familia de tipo. Puramente uma dica visual pra varredura da lista
 *  — o nome do arquivo continua sendo a informacao. */
function icone(contentType?: string): string {
  if (!contentType) return "📎";
  if (contentType.startsWith("image/")) return "🖼️";
  if (contentType === "application/pdf") return "📄";
  if (contentType.includes("zip") || contentType.includes("compressed")) return "🗜️";
  if (contentType.includes("sheet") || contentType.includes("excel") || contentType.includes("csv")) return "📊";
  return "📎";
}

function LinhaAnexo({ doc, prefeituraId }: { doc: DocumentoCargaCadastro; prefeituraId: number }) {
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState(false);

  // Baixa via SDK (a rota exige `Authorization: Bearer`, e um <a href> nao manda
  // header) e entrega pelo anchor sintetico. Mesmo caminho do download da CCB.
  //
  // O `Content-Disposition` que a API manda nao vale aqui: object URL de blob
  // ignora o header, entao o nome do arquivo vem do atributo `download`.
  async function baixar() {
    setBaixando(true);
    setErro(false);
    let url: string | null = null;
    try {
      const blob = await atlas.admin.prefeituraDocumentoCargaBlob(prefeituraId, doc.id);
      url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.nome;
      a.click();
    } catch {
      setErro(true);
    } finally {
      // Revoga no fim: o clique sincrono acima ja registrou o download, e manter
      // a URL viva vazaria memoria a cada anexo baixado no mesmo modal.
      if (url) URL.revokeObjectURL(url);
      setBaixando(false);
    }
  }

  const meta = [doc.contentType, tamanhoLegivel(doc.tamanho)].filter(Boolean).join(" · ");

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
        background: "var(--bg-elev)", border: "1px solid var(--border)", borderRadius: 8,
      }}
    >
      <span style={{ fontSize: 16, flex: "none" }}>{icone(doc.contentType)}</span>
      {/* minWidth:0 pro overflow do nome funcionar dentro do flex */}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.nome}>
          {doc.nome}
        </div>
        {meta ? <div style={{ fontSize: 10, color: "var(--text-dim)" }}>{meta}</div> : null}
      </div>
      <button
        type="button"
        onClick={() => void baixar()}
        disabled={baixando}
        style={{
          flex: "none", fontSize: 11, padding: "5px 10px", borderRadius: 6,
          border: "1px solid var(--border)", background: "var(--bg-elev-2)",
          color: erro ? "var(--danger-500)" : "var(--accent)",
          cursor: baixando ? "default" : "pointer", font: "inherit", fontFamily: "inherit",
        }}
      >
        {baixando ? "Baixando…" : erro ? "Tentar de novo" : "Baixar"}
      </button>
    </div>
  );
}

export function DocumentosCarga({
  prefeituraId,
  /** Cadastro veio da esteira do site. Fora dela o Passo 6 nunca aconteceu, e o
   *  bloco todo nao se aplica — mesma regra do `veioDoSite` de
   *  `DocumentosCadastro`. */
  veioDoSite,
}: {
  prefeituraId: number;
  veioDoSite: boolean;
}) {
  const q = useQuery({
    queryKey: ["admin", "prefeituras", prefeituraId, "documentos-carga"],
    queryFn: () => atlas.admin.prefeituraDocumentosCarga(prefeituraId),
    enabled: veioDoSite,
  });

  if (!veioDoSite) return null;

  const documentos = q.data?.documentos ?? [];

  return (
    <div style={{ padding: 14, background: "var(--bg-elev-2)", borderRadius: 10, border: "1px solid var(--border)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase", marginBottom: 10 }}>
        Documentos de carga
        {documentos.length > 0 ? ` (${documentos.length})` : ""}
      </div>

      {q.isPending ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>carregando…</div>
      ) : q.isError ? (
        // Distinto do estado vazio: "nao anexou" e "nao consegui ler" levam o
        // operador a acoes opostas, e um erro silencioso aqui pareceria cadastro
        // sem anexo nenhum.
        <div style={{ fontSize: 12, color: "var(--danger-500)" }}>
          Não foi possível carregar os anexos de carga.
        </div>
      ) : documentos.length === 0 ? (
        // Sem "⚠": o Passo 6 e' opcional por definicao, entao lista vazia aqui
        // e' cadastro normal — nao pendencia.
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          Nenhum arquivo de convênio foi anexado — a etapa é opcional.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 10 }}>
            Arquivos do convênio enviados pelo solicitante no cadastro do site.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {documentos.map((doc) => (
              <LinhaAnexo key={doc.id} doc={doc} prefeituraId={prefeituraId} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
